// Multi-cell selection on the crew grid (#589): which cells a rectangle
// covers, which of them may be written, and where a pasted block lands.
//
// Pulled out of the page so the rules are assertable — apps/web runs vitest in
// a node environment with no DOM, and these are the parts that would break
// silently. No runtime imports beyond the date helpers and the row model.
//
// ── Partial apply, never silent ──────────────────────────────────────────────
//
// A selection is a rectangle drawn over whatever rows happen to be on screen,
// so it routinely crosses rows the scheduler cannot write: a maintenance parent
// sits between its own sub-projects, and a fab-scoped editor's Fabrication jobs
// are interleaved with everyone else's on the All lens. Refusing the whole
// operation for one such row would make dragging across a maintenance group
// impossible. So the allowed cells are written and every refused cell is
// REPORTED — marked on the grid before anything is applied, and listed by job,
// line and date afterwards (`describeRefusals`). Nothing is skipped quietly.
//
// The server stays all-or-nothing (`bulkUpsertDraftEntries` refuses the whole
// batch on any maintenance parent or out-of-scope job), so a cell this module
// lets through by mistake fails the whole request loudly rather than landing.

import type { CrewPhase, CrewRow } from './scheduleModel';
import { isFabProject } from './scheduleModel';
import { addDays, isWeekendYmd, localYmd } from './gridDates';
import { parseLocalDate } from '@/lib/dates';

/** One cell: a crew line (CrewRow.key) on a day. */
export interface CellRef {
  rowKey: string;
  ds: string;
}

/** A drag or shift-click: the cell it started on, and the one it reached. */
export interface Selection {
  anchor: CellRef;
  focus: CellRef;
}

/**
 * Why a cell won't be written.
 *
 * - `maintenance-parent`, `fab-scope` — the SINGLE-CELL path refuses these too
 *   (`singleCellRefusal`), so bulk can never reach a cell typing can't.
 * - `past` — bulk only. Typing into one past day is how a scheduler corrects
 *   history; a dragged rectangle that happens to start last week is not. Crew
 *   setup (setupPlan) and ⇄ Shift (shiftPlan) both leave days before today
 *   alone for the same reason, so the three bulk tools agree.
 */
export type Refusal = 'maintenance-parent' | 'fab-scope' | 'past';

export const REFUSAL_TEXT: Record<Refusal, string> = {
  'maintenance-parent': 'maintenance job — crew goes on its sub-projects',
  'fab-scope': 'outside your Fabrication-only edit role',
  past: 'before today — bulk edits leave booked history alone; type into a single day to correct it',
};

export interface RefusalContext {
  /** FabManpowerEditor: may write Fabrication-division jobs only. */
  fabEditOnly: boolean;
  /** Today's Eastern business date, YYYY-MM-DD. */
  today: string;
}

/**
 * A fab-scoped editor's row rule: Fabrication-division jobs only. The one
 * definition — ManpowerPage's row controls (the − N + stepper, Set up, the
 * row menu) gate on it too, so cells, bulk edits and row actions cannot
 * disagree about which jobs a fab editor may touch.
 */
export function outsideFabScope(project: Parameters<typeof isFabProject>[0], fabEditOnly: boolean): boolean {
  return fabEditOnly && !isFabProject(project);
}

/**
 * What the click-to-type path refuses. ManpowerPage gates the single cell on
 * exactly this, so the bulk path (below) is a superset by construction rather
 * than by two lists agreeing.
 */
export function singleCellRefusal(row: CrewRow, ctx: Pick<RefusalContext, 'fabEditOnly'>): Refusal | null {
  if (row.project.isMaintenance) return 'maintenance-parent';
  if (outsideFabScope(row.project, ctx.fabEditOnly)) return 'fab-scope';
  return null;
}

/** What a bulk edit, clear or paste refuses: everything above, plus the past. */
export function bulkCellRefusal(row: CrewRow, ds: string, ctx: RefusalContext): Refusal | null {
  return singleCellRefusal(row, ctx) ?? (ds < ctx.today ? 'past' : null);
}

/**
 * The rectangle a selection covers, as index ranges into the rows and days on
 * screen. Null when either end is no longer on screen — a filter or a window
 * change took its row or day away — so a stale selection simply disappears
 * rather than snapping to some other rectangle.
 */
export function selectionBounds(
  sel: Selection,
  rowKeys: readonly string[],
  days: readonly string[],
): { r0: number; r1: number; c0: number; c1: number } | null {
  const ra = rowKeys.indexOf(sel.anchor.rowKey);
  const rf = rowKeys.indexOf(sel.focus.rowKey);
  const ca = days.indexOf(sel.anchor.ds);
  const cf = days.indexOf(sel.focus.ds);
  if (ra < 0 || rf < 0 || ca < 0 || cf < 0) return null;
  return { r0: Math.min(ra, rf), r1: Math.max(ra, rf), c0: Math.min(ca, cf), c1: Math.max(ca, cf) };
}

/**
 * The days of a selection the operation acts on.
 *
 * ── Weekends (#589 Q3) ──
 * Crews staff Mon–Fri by default (the grid shades Sat/Sun and the bid forecast
 * claims no crew on them), so "Skip Sat/Sun" is ON by default and then the
 * weekend columns inside a rectangle are simply not part of it: a set or clear
 * leaves them alone, a copy leaves them out, and a paste flows over weekdays
 * only. One rule for all four, so a block copied Mon–Fri and pasted on a
 * Thursday lands Thu, Fri, Mon, Tue, Wed — what a scheduler means — rather
 * than putting crew on Saturday and Sunday.
 *
 * Off, every column counts day-for-day, which is what a six- or seven-day job
 * (CrewSetupModal's day-of-week sets) needs.
 */
export function selectionDays(days: readonly string[], c0: number, c1: number, skipWeekends: boolean): string[] {
  return days.slice(c0, c1 + 1).filter((ds) => !skipWeekends || !isWeekendYmd(ds));
}

/** Every cell of a selection, row-major, with the weekend rule applied. */
export function selectedCells(
  sel: Selection,
  rowKeys: readonly string[],
  days: readonly string[],
  skipWeekends: boolean,
): CellRef[] {
  const b = selectionBounds(sel, rowKeys, days);
  if (!b) return [];
  const cols = selectionDays(days, b.c0, b.c1, skipWeekends);
  const out: CellRef[] = [];
  for (let r = b.r0; r <= b.r1; r++) for (const ds of cols) out.push({ rowKey: rowKeys[r], ds });
  return out;
}

/** Weekend cells inside the rectangle that "Skip Sat/Sun" leaves out — shown, not hidden. */
export function skippedWeekendCount(sel: Selection, rowKeys: readonly string[], days: readonly string[]): number {
  const b = selectionBounds(sel, rowKeys, days);
  if (!b) return 0;
  const weekendCols = days.slice(b.c0, b.c1 + 1).filter(isWeekendYmd).length;
  return weekendCols * (b.r1 - b.r0 + 1);
}

/** A cell to write, with the value it gets. */
export interface CellWrite extends CellRef {
  value: number;
}

export interface Placement {
  writes: CellWrite[];
  /** Clipboard rows that ran past the last row on screen. */
  clippedRows: number;
  /** Clipboard columns that ran past the last day in the window. */
  clippedCols: number;
}

/**
 * Where a pasted block lands, from the selection's top-left cell.
 *
 * - Rows map one-to-one onto the rows ON SCREEN, in screen order. A row that
 *   can't be written (a maintenance parent, say) still takes its clipboard row
 *   and is then refused and reported — skipping it would shift every row below
 *   onto the wrong job.
 * - Columns follow the weekend rule (see `selectionDays`). With Skip Sat/Sun
 *   on, an anchor on a weekend starts at the next weekday.
 * - Anything past the last row or day on screen is CLIPPED and counted, never
 *   written blind: those cells aren't loaded, so neither their current values
 *   nor whether they may be written is known here.
 * - A single value pasted onto a larger selection fills the selection, as in a
 *   spreadsheet.
 *
 * `matrix` cells may be null: a ragged row from a non-spreadsheet source.
 * Those are left unchanged. (An EMPTY cell from Excel arrives as 0 — see
 * crewClipboard — because pasting a blank over a spreadsheet cell clears it.)
 */
export function placeBlock(opts: {
  matrix: (number | null)[][];
  sel: Selection;
  rowKeys: readonly string[];
  days: readonly string[];
  skipWeekends: boolean;
}): Placement {
  const { matrix, sel, rowKeys, days, skipWeekends } = opts;
  const b = selectionBounds(sel, rowKeys, days);
  if (!b || matrix.length === 0) return { writes: [], clippedRows: 0, clippedCols: 0 };

  // Fill: one value over a larger selection.
  if (matrix.length === 1 && matrix[0].length === 1) {
    const v = matrix[0][0];
    if (v == null) return { writes: [], clippedRows: 0, clippedCols: 0 };
    return {
      writes: selectedCells(sel, rowKeys, days, skipWeekends).map((c) => ({ ...c, value: v })),
      clippedRows: 0,
      clippedCols: 0,
    };
  }

  const targetDays = days.slice(b.c0).filter((ds) => !skipWeekends || !isWeekendYmd(ds));
  // Columns that carry a figure — the null padding of a ragged row isn't
  // pasted either way, so it isn't "clipped" when it runs off the window.
  const width = matrix.reduce((w, r) => {
    let k = r.length;
    while (k > 0 && r[k - 1] == null) k--;
    return Math.max(w, k);
  }, 0);
  const writes: CellWrite[] = [];
  for (let i = 0; i < matrix.length; i++) {
    const rowKey = rowKeys[b.r0 + i];
    if (rowKey === undefined) break;
    matrix[i].forEach((value, j) => {
      const ds = targetDays[j];
      if (ds !== undefined && value != null) writes.push({ rowKey, ds, value });
    });
  }
  return {
    writes,
    clippedRows: Math.max(0, matrix.length - (rowKeys.length - b.r0)),
    clippedCols: Math.max(0, width - targetDays.length),
  };
}

/** A refused cell, carrying what the report needs to name it. */
export interface RefusedCell extends CellRef {
  reason: Refusal;
  /** How the scheduler knows the job — see jobLabel. */
  label: string;
  phase: CrewPhase;
  /** The figure the refused write would have put there. */
  value: number;
}

/**
 * A job as the grid names it, for anything the scheduler reads.
 *
 * ⚠️ A maintenance SUB-PROJECT's `jobNumber` is a generated placeholder
 * (`MNT-3fa9…`) that the grid never shows — its row reads "↳ sub" under the
 * parent. Nobody can find a row by that number, so a sub is named the way the
 * grid names it: the parent's job number, then ↳ and its own title.
 */
export function jobLabel(project: CrewRow['project'], parentJobNumber: string | null): string {
  if (project.parentProjectId != null) return `${parentJobNumber ? `${parentJobNumber} ` : ''}↳ ${project.title}`;
  return `${project.jobNumber} ${project.title}`;
}

/** Job numbers by project id, for naming a sub's parent (rowJobLabel). */
export function jobNumbersById(rows: Iterable<CrewRow>): Map<number, string> {
  return new Map([...rows].map((r) => [r.projectId, r.project.jobNumber]));
}

/** A crew line's job as the grid names it — jobLabel, with the parent found. */
export function rowJobLabel(row: CrewRow, jobNumberById: ReadonlyMap<number, string>): string {
  const parent = row.project.parentProjectId;
  return jobLabel(row.project, parent != null ? jobNumberById.get(parent) ?? null : null);
}

/** Split writes into the ones that go and the ones that are refused. */
export function partitionWrites(
  writes: readonly CellWrite[],
  rowsByKey: ReadonlyMap<string, CrewRow>,
  ctx: RefusalContext,
): { allowed: (CellWrite & { row: CrewRow })[]; refused: RefusedCell[] } {
  const allowed: (CellWrite & { row: CrewRow })[] = [];
  const refused: RefusedCell[] = [];
  let jobNumberById: Map<number, string> | null = null; // built only if something is refused
  for (const w of writes) {
    const row = rowsByKey.get(w.rowKey);
    if (!row) continue;
    const reason = bulkCellRefusal(row, w.ds, ctx);
    if (!reason) { allowed.push({ ...w, row }); continue; }
    jobNumberById ??= jobNumbersById(rowsByKey.values());
    refused.push({
      rowKey: w.rowKey, ds: w.ds, reason, phase: row.phase, value: w.value,
      label: rowJobLabel(row, jobNumberById),
    });
  }
  return { allowed, refused };
}

/** "Oct 8" — local calendar day, through the app's date-only parser. */
const md = (ymd: string) =>
  parseLocalDate(ymd)!.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** Calendar-consecutive runs of dates: ["Oct 12–16", "Oct 19"]. */
function dateRuns(dates: string[]): string[] {
  const sorted = [...new Set(dates)].sort();
  const runs: string[][] = [];
  for (const d of sorted) {
    const last = runs[runs.length - 1];
    const prev = last?.[last.length - 1];
    if (prev && localYmd(addDays(parseLocalDate(prev)!, 1)) === d) { last.push(d); continue; }
    runs.push([d]);
  }
  return runs.map((r) => {
    if (r.length === 1) return md(r[0]);
    const a = r[0];
    const z = r[r.length - 1];
    return a.slice(0, 7) === z.slice(0, 7) ? `${md(a)}–${Number(z.slice(8, 10))}` : `${md(a)} – ${md(z)}`;
  });
}

/**
 * The refusal report: one line per job, crew line and reason, naming every
 * date. "Exactly which cells didn't change" is the requirement — a count alone
 * would leave the scheduler hunting for them.
 */
export function describeRefusals(refused: readonly RefusedCell[]): string[] {
  const groups = new Map<string, RefusedCell[]>();
  for (const r of refused) {
    const k = `${r.rowKey}|${r.reason}`;
    const arr = groups.get(k);
    if (arr) arr.push(r);
    else groups.set(k, [r]);
  }
  return [...groups.values()].map((cells) => {
    const c = cells[0];
    const line = c.phase === 'fabrication' ? ' (Fabrication)' : c.phase === 'installation' ? ' (Installation)' : '';
    return `${c.label}${line}: ${dateRuns(cells.map((x) => x.ds)).join(', ')} — ${REFUSAL_TEXT[c.reason]}`;
  });
}
